/* Global ErrorBoundary — catches any React render/lifecycle crash that would
 * otherwise unmount the whole tree and leave the user with a blank white
 * screen (a real reported symptom on iOS Safari after Sandbox → Save).
 *
 * On error:
 *  • Show a friendly recovery UI with the error text (helps us debug field
 *    reports without needing the DevTools console).
 *  • "Reload page" button — one-tap recovery for the user.
 *  • Best-effort console.error so the crash still surfaces in DevTools.
 */
import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, message: (err && (err.message || String(err))) || "Unknown error" };
  }

  componentDidCatch(err, info) {
    // Log to console for the DevTools remote-debugger flow on iOS.
    console.error("[RecipeFiler] Uncaught render error:", err, info?.componentStack);
    // Best-effort silent report to server so admins can see live issues in
    // the Admin → Error Log tab. Uses raw fetch (not axios) so a crashed
    // axios instance can't take this down too.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      if (backend) {
        fetch(`${backend}/api/errors/report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            kind: "react",
            message: (err && (err.message || String(err))) || "Unknown error",
            stack: (err && err.stack) || info?.componentStack || null,
            page: typeof window !== "undefined" ? window.location.pathname : null,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          }),
        }).catch(() => {});
      }
    } catch { /* silent */ }
  }

  handleReload = () => {
    try {
      // Full reload wipes any half-torn-down React state.
      window.location.reload();
    } catch { /* ignore */ }
  };

  handleHome = () => {
    try {
      window.location.href = "/";
    } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen flex flex-col items-center justify-center bg-rf-cream px-6 py-12 text-center"
      >
        <div className="max-w-md w-full">
          <div className="text-6xl mb-4" aria-hidden>🧑‍🍳</div>
          <h1 className="font-display font-black text-3xl text-rf-ink mb-3 leading-tight">
            Something went wrong in the kitchen.
          </h1>
          <p className="text-rf-ink-soft leading-relaxed mb-6">
            The page hit an unexpected error and stopped loading. Your recipe was
            saved — you can safely reload and pick up where you left off.
          </p>
          {this.state.message && (
            <p className="text-xs text-rf-ink-soft/80 italic mb-6 break-words">
              <span className="font-bold">Details:</span> {this.state.message}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              data-testid="error-boundary-reload"
              onClick={this.handleReload}
              className="btn-amber justify-center"
            >
              Reload page
            </button>
            <button
              type="button"
              data-testid="error-boundary-home"
              onClick={this.handleHome}
              className="btn-outline-ink justify-center"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
